import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { settingsTargetPath } from "@/features/settings/settingsTargets"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconAlertTriangle, IconTrash } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { Send, Trash2, X } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"
import {
  isRecipientOverlapError,
  isSupplierWarningError,
  recipientOverlaps,
  sendInquiryDirect,
  signoffPlaceholderLines,
  supplierWarningNotes,
  type RecipientOverlap,
} from "../../../api/inquiryDraftApi"
import type { NoteOut } from "../../../api/notesApi"
import {
  discardInquiryDirect,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { DetailPanelHeader } from "../../shared/DetailPanelHeader"
import { FooterActionBar } from "../../shared/FooterActionBar"
import {
  SupplierWarningDialog,
  type SupplierWarningEntry,
} from "../../shared/notes/SupplierWarningDialog"
import {
  SignoffPlaceholderDialog,
  type SignoffLines,
} from "./draft/SignoffPlaceholderDialog"
import { InquiryBulkSelectionList } from "./InquiryBulkSelectionList"
import { RecipientOverlapDialog } from "./RecipientOverlapDialog"

// Cap in-flight sends so a large selection can't fire dozens of SES calls at once.
const CONCURRENCY = 5

/** What the server held a draft's send back for, pending the user's decision: its
 *  supplier's warning notes (MC_NOTES), other suppliers' inquiries to the same
 *  address (TUR-1812), or sign-off lines still holding placeholder text. The server
 *  checks them in that order, so one refusal is one kind. */
interface Refusal {
  notes: NoteOut[]
  overlaps: RecipientOverlap[]
  signoff: SignoffLines | null
}

/** Everything the user confirmed for one draft so far: every retry carries all of it,
 *  so a draft confirmed for its warning and then refused for an overlap does not ask
 *  about the warning again. */
interface Acks {
  noteIds?: string[]
  inquiryIds?: string[]
  signoff?: boolean
}

/**
 * One draft's send. A refusal that waits for the user is neither sent nor failed: it
 * is noted in `refused` and still rethrown, so runBulk files it with the unsent ones.
 * `acks` is set only on a retry after the user confirmed.
 */
async function sendNoting(
  id: string,
  refused: Map<string, Refusal>,
  acks?: Acks,
) {
  try {
    await (acks
      ? sendInquiryDirect(id, {
          ...(acks.noteIds ? { acknowledgedNoteIds: acks.noteIds } : {}),
          ...(acks.inquiryIds
            ? { acknowledgedInquiryIds: acks.inquiryIds }
            : {}),
          ...(acks.signoff ? { acknowledgePlaceholderSignature: true } : {}),
        })
      : sendInquiryDirect(id))
  } catch (error) {
    const signoff = signoffPlaceholderLines(error)
    if (isSupplierWarningError(error)) {
      refused.set(id, {
        notes: supplierWarningNotes(error),
        overlaps: [],
        signoff: null,
      })
    } else if (isRecipientOverlapError(error)) {
      refused.set(id, {
        notes: [],
        overlaps: recipientOverlaps(error),
        signoff: null,
      })
    } else if (signoff) {
      refused.set(id, { notes: [], overlaps: [], signoff })
    }
    throw error
  }
}

/** A draft held back for the user's decision, with what was confirmed for it so far. */
interface HeldDraft extends Refusal {
  inquiryId: string
  supplierName: string
  acks: Acks
}

export async function runBulk(
  ids: string[],
  worker: (id: string) => Promise<void>,
): Promise<{ succeeded: string[]; failed: string[] }> {
  const succeeded: string[] = []
  const failed: string[] = []
  let cursor = 0
  async function drain() {
    while (cursor < ids.length) {
      const id = ids[cursor++]
      try {
        await worker(id)
        succeeded.push(id)
      } catch {
        failed.push(id)
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, ids.length) }, drain),
  )
  return { succeeded, failed }
}

/** The placeholder-signature question for held drafts; an edit leaves them selected
 *  and opens the setting that holds the lines. */
function BulkSignoffDialog({
  lines,
  onSendAnyway,
  onLeave,
  isLoading,
}: {
  lines: SignoffLines
  onSendAnyway: () => void
  onLeave: () => void
  isLoading: boolean
}) {
  const navigate = useNavigate()
  return (
    <SignoffPlaceholderDialog
      lines={lines}
      onSendAnyway={onSendAnyway}
      onEdit={(target) => {
        onLeave()
        navigate(settingsTargetPath(target))
      }}
      onCancel={onLeave}
      isLoading={isLoading}
    />
  )
}

interface InquiryBulkActionsPanelProps {
  /** The checked draft rows, captured at click time — no id resolution here. */
  selectedRows: SupplierInquiryListItem[]
  onRemoveIds: (ids: string[]) => void
  onClearSelection: () => void
}

/**
 * Replaces the detail pane while draft inquiries are multi-selected (same shape as
 * SendInquiryPanel). Sends/discards each draft independently: succeeded items leave
 * the selection, failures stay selected for retry.
 */
export function InquiryBulkActionsPanel({
  selectedRows,
  onRemoveIds,
  onClearSelection,
}: InquiryBulkActionsPanelProps) {
  const { translate } = useTranslation()
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState<"send" | "discard" | null>(null)
  const [running, setRunning] = useState(false)
  // Drafts whose send the server held back for a supplier warning (MC_NOTES) or a
  // recipient overlap (TUR-1812): nothing went out for them, so the user decides.
  // Warnings are asked first, overlaps after. `sent` and `failed` are what the same
  // run did for the rest, kept until the decision so the result is reported once.
  const [held, setHeld] = useState<{
    drafts: HeldDraft[]
    sent: number
    failed: number
  } | null>(null)
  const warnedDrafts = held?.drafts.filter((d) => d.notes.length > 0) ?? []
  const overlapDrafts = held?.drafts.filter((d) => d.overlaps.length > 0) ?? []
  // The dialog on screen asks about these: warnings first, then overlaps, then
  // placeholder sign-offs.
  const askedDrafts =
    warnedDrafts.length > 0
      ? warnedDrafts
      : overlapDrafts.length > 0
        ? overlapDrafts
        : (held?.drafts ?? [])

  const ids = selectedRows.map((row) => row.id)
  const count = ids.length

  // Once per batch: the direct helpers skip per-call invalidation on purpose.
  function invalidateAfter(action: "send" | "discard") {
    qc.invalidateQueries({ queryKey: ["mc", "inquiries", "list"] })
    qc.invalidateQueries({ queryKey: ["mc", "inquiries", "stats"] })
    // The detail pane caches each inquiry on its own key; without this a draft
    // just sent here still reads "Draft" there until it is opened again.
    qc.invalidateQueries({ queryKey: ["mc", "inquiries", "full-detail"] })
    // A bulk send moves the "Datenlücken" tile's open-clarifications bucket
    // (TUR-1539), same as useSendInquiry's single-send path. A discard only
    // drops an unsent draft, which never counted as an open clarification.
    if (action === "send") {
      qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
    }
  }

  function reportResult(
    action: "send" | "discard",
    succeeded: number,
    failed: number,
  ) {
    if (failed === 0) {
      toast.success(
        translate(
          action === "send"
            ? "MC_INQ_BULK_SEND_RESULT"
            : "MC_INQ_BULK_DISCARD_RESULT",
          { count: succeeded },
        ),
      )
    } else {
      // One key for the whole "N sent, M failed" sentence, so it stays localizable.
      toast.error(
        translate(
          action === "send"
            ? "MC_INQ_BULK_SEND_PARTIAL"
            : "MC_INQ_BULK_DISCARD_PARTIAL",
          { succeeded, failed },
        ),
      )
    }
  }

  // The drafts a run held back, each with what was confirmed for it before this run.
  function heldDrafts(
    refused: Map<string, Refusal>,
    acks: Map<string, Acks> = new Map(),
  ): HeldDraft[] {
    return [...refused].map(([inquiryId, refusal]) => ({
      inquiryId,
      supplierName:
        selectedRows.find((row) => row.id === inquiryId)?.supplierName ?? "",
      ...refusal,
      acks: acks.get(inquiryId) ?? {},
    }))
  }

  async function performBulk(action: "send" | "discard") {
    setRunning(true)
    const refused = new Map<string, Refusal>()
    const { succeeded, failed: unsent } = await runBulk(
      ids,
      action === "send"
        ? (id) => sendNoting(id, refused)
        : discardInquiryDirect,
    )
    const failed = unsent.filter((id) => !refused.has(id))

    // A discarded draft no longer exists: drop its cached detail rather than
    // refetching it into a 404.
    if (action === "discard") {
      for (const id of succeeded) {
        qc.removeQueries({ queryKey: ["mc", "inquiries", "full-detail", id] })
      }
    }
    invalidateAfter(action)

    // Deselect the ones that went through; keep failures selected so they stay visible.
    onRemoveIds(succeeded)
    setRunning(false)
    setConfirm(null)

    if (refused.size > 0) {
      setHeld({
        drafts: heldDrafts(refused),
        sent: succeeded.length,
        failed: failed.length,
      })
      return
    }
    reportResult(action, succeeded.length, failed.length)
  }

  // "Send all anyway" for the dialog on screen: the warning dialog retries the drafts
  // held for a warning, the overlap dialog the rest. Each retry carries everything
  // confirmed for that draft so far. A draft refused again (a note or an overlap that
  // appeared meanwhile, or an overlap after its warning) comes back to the dialog.
  async function sendDespiteHeld() {
    if (!held || running) return
    const { drafts, sent, failed: failedBefore } = held
    const asked = askedDrafts
    const waiting = drafts.filter((d) => !asked.includes(d))
    const acks = new Map(
      asked.map((d) => [
        d.inquiryId,
        {
          ...d.acks,
          ...(d.notes.length > 0
            ? { noteIds: d.notes.map((note) => note.id) }
            : {}),
          ...(d.overlaps.length > 0
            ? { inquiryIds: [...new Set(d.overlaps.map((o) => o.inquiryId))] }
            : {}),
          ...(d.signoff ? { signoff: true } : {}),
        },
      ]),
    )
    setRunning(true)
    const refused = new Map<string, Refusal>()
    const { succeeded, failed: unsent } = await runBulk(
      [...acks.keys()],
      (id) => sendNoting(id, refused, acks.get(id)),
    )
    const failed = unsent.filter((id) => !refused.has(id))
    invalidateAfter("send")
    onRemoveIds(succeeded)
    setRunning(false)
    const totals = {
      sent: sent + succeeded.length,
      failed: failedBefore + failed.length,
    }
    const stillHeld = [...heldDrafts(refused, acks), ...waiting]
    if (stillHeld.length > 0) {
      setHeld({ drafts: stillHeld, ...totals })
      return
    }
    setHeld(null)
    reportResult("send", totals.sent, totals.failed)
  }

  // Skip drops the drafts of the dialog on screen from the selection; cancel leaves
  // them selected. Neither sends anything. Leaving the warning dialog does not touch
  // the drafts held for an overlap: their dialog comes next, they were never shown.
  // Once nothing is held any more, the run's result is reported.
  function leaveHeld(skipped: boolean) {
    if (!held) return
    const { drafts, sent, failed } = held
    const left = askedDrafts
    const waiting = drafts.filter((d) => !left.includes(d))
    if (skipped) {
      onRemoveIds(left.map((d) => d.inquiryId))
      toast.warning(
        translate(
          warnedDrafts.length > 0
            ? "MC_NOTE_SEND_SKIPPED"
            : "MC_INQ_OVERLAP_SKIPPED",
          { count: left.length },
        ),
      )
    }
    if (waiting.length > 0) {
      setHeld({ drafts: waiting, sent, failed })
      return
    }
    setHeld(null)
    if (sent > 0 || failed > 0) reportResult("send", sent, failed)
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <DetailPanelHeader
        label={translate("MC_INQ_BULK_TITLE")}
        title={translate("MC_INQ_BULK_SELECTED", { count })}
        actions={({ iconOnly }) => (
          <Button
            size="sm"
            variant="outline"
            className="h-[30px] gap-1.5 text-[12.5px]"
            onClick={onClearSelection}
            disabled={running}
            aria-label={translate("MC_INQ_BULK_CLEAR")}
            title={translate("MC_INQ_BULK_CLEAR")}
          >
            <X size={13} />
            {!iconOnly && translate("MC_INQ_BULK_CLEAR")}
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto p-4">
        <InquiryBulkSelectionList
          selectedRows={selectedRows}
          onRemoveIds={onRemoveIds}
        />
      </div>

      <FooterActionBar left={translate("MC_INQ_BULK_HINT")}>
        <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
          <Button
            size="sm"
            variant="outline"
            className="border-red1/40 text-[12.5px] text-red1"
            onClick={() => setConfirm("discard")}
            disabled={running}
          >
            <Trash2 size={13} />
            {translate("MC_INQ_BULK_DISCARD")}
          </Button>
        </PermissionGate>
        <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
          <Button
            size="sm"
            className="bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
            onClick={() => setConfirm("send")}
            disabled={running}
          >
            <Send size={13} />
            {translate("MC_INQ_BULK_SEND")}
          </Button>
        </PermissionGate>
      </FooterActionBar>

      {held && warnedDrafts.length > 0 && (
        <SupplierWarningDialog
          mode="bulk"
          warnings={warnedDrafts satisfies SupplierWarningEntry[]}
          onConfirm={() => void sendDespiteHeld()}
          onSkip={() => leaveHeld(true)}
          onCancel={() => leaveHeld(false)}
          isLoading={running}
        />
      )}
      {held && warnedDrafts.length === 0 && overlapDrafts.length > 0 && (
        <RecipientOverlapDialog
          mode="bulk"
          entries={overlapDrafts}
          onConfirm={() => void sendDespiteHeld()}
          onSkip={() => leaveHeld(true)}
          onCancel={() => leaveHeld(false)}
          isLoading={running}
        />
      )}
      {held && warnedDrafts.length === 0 && overlapDrafts.length === 0 && (
        <BulkSignoffDialog
          lines={{
            signature: [
              ...new Set(
                held.drafts.flatMap((d) => d.signoff?.signature ?? []),
              ),
            ],
            footer: [
              ...new Set(held.drafts.flatMap((d) => d.signoff?.footer ?? [])),
            ],
          }}
          onSendAnyway={() => void sendDespiteHeld()}
          onLeave={() => leaveHeld(false)}
          isLoading={running}
        />
      )}
      {confirm === "send" && (
        <ConfirmDialog
          title={translate("MC_INQ_BULK_SEND_CONFIRM_TITLE")}
          message={translate("MC_INQ_BULK_SEND_CONFIRM_DESC", { count })}
          confirmLabel={translate("MC_INQ_BULK_SEND")}
          cancelLabel={translate("MC_DECL_BTN_CANCEL")}
          variant="warning"
          icon={IconAlertTriangle}
          isLoading={running}
          onConfirm={() => performBulk("send")}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === "discard" && (
        <ConfirmDialog
          title={translate("MC_INQ_BULK_DISCARD_CONFIRM_TITLE")}
          message={translate("MC_INQ_BULK_DISCARD_CONFIRM_DESC", { count })}
          confirmLabel={translate("MC_INQ_BULK_DISCARD")}
          cancelLabel={translate("MC_DECL_BTN_CANCEL")}
          variant="danger"
          icon={IconTrash}
          isLoading={running}
          onConfirm={() => performBulk("discard")}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
